const AUTH_URL_ENDPOINTS = {
  shopee: '/auth/api/v1/marketplaces/shopee/auth/url',
  tiktok: '/auth/api/v1/marketplaces/tiktok/auth/url',
  lazada: '/auth/api/v1/marketplaces/lazada/auth/url',
}

/**
 * @typedef {Object} MarketplaceIntegrationVm
 * @property {{ post: (url: string) => Promise<{ data?: { data?: { auth_url?: string } } }> }} $http_new
 * @property {(opts: { message: string }) => void} [$toast_error]
 */

/**
 * Calculates responsive popup window features for marketplace OAuth authentication.
 * @returns {string} Window features string for `window.open`.
 */
export const getIntegrationPopupWindowFeatures = () => {
  if (typeof window === 'undefined') {
    return 'width=1050,height=800,menubar=no,toolbar=no,location=no,status=no'
  }

  const { width, height } = window.screen

  const popupWidth = Math.max(320, Math.min(1300, width - 32))
  const popupHeight = Math.max(480, Math.min(800, height - 32))
  const popupLeft = Math.max(0, Math.floor((width - popupWidth) / 2))
  const popupTop = Math.max(0, Math.floor((height - popupHeight) / 2))

  return `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop},menubar=no,toolbar=no,location=no,status=no`
}

/**
 * Composable for managing marketplace authentication flow, popup windows, and BroadcastChannel events.
 * @param {MarketplaceIntegrationVm} vm - Vue instance providing `$http_new` and optionally `$toast_error`.
 */
export default function useMarketplaceIntegrationRequest(vm) {
  /**
   * Returns the auth URL endpoint for the given marketplace.
   * @param {string} marketplace
   * @returns {string|undefined}
   */
  const getMarketplaceAuthEndpoint = marketplace => AUTH_URL_ENDPOINTS[marketplace]

  /**
   * Tracks currently open authentication popup windows.
   */
  const activeAuthWindows = []

  /**
   * Closes all currently open authentication popup windows.
   */
  const closeAllActiveAuthWindows = () => {
    while (activeAuthWindows.length > 0) {
      const win = activeAuthWindows.pop()
      if (win && !win.closed) {
        try {
          win.close()
        } catch (e) {
          // ignore
        }
      }
    }
  }

  let bc = null

  const handleIntegrationMessage = event => {
    if (!event.data) return
    const { type, data } = event.data

    // Close any active intermediate auth windows (e.g. Tab B)
    closeAllActiveAuthWindows()

    // Failure in popup or tab: show error toast on current dashboard
    if (type === 'INTEGRATION_FAILED') {
      if (vm?.$toast_error) {
        vm.$toast_error({
          message: 'Integrasi marketplace gagal, silahkan coba lagi',
        })
      }
      return
    }

    // Success in popup or tab: reload/refresh dashboard state
    if (type === 'INTEGRATION_SUCCESS' || !type) {
      window.location.reload()
    }
  }

  /**
   * Initializes BroadcastChannel listener for marketplace integration.
   */
  const initBroadcastChannel = () => {
    try {
      if (!bc) {
        bc = new BroadcastChannel('marketplace_integration')
        bc.onmessage = handleIntegrationMessage
      }
    } catch (e) {
      console.error('BroadcastChannel initialization error:', e)
    }
  }

  /**
   * Closes BroadcastChannel.
   */
  const closeBroadcastChannel = () => {
    if (bc) {
      bc.onmessage = null
      bc.close()
      bc = null
    }
  }

  /**
   * Opens an authentication popup or tab and ensures integration message listeners are active.
   * @param {string} authUrl - The OAuth authorization URL to open.
   * @param {{ asTab?: boolean, marketplace?: string, windowFeatures?: string }|boolean} [options] - Configuration options or boolean `asTab`.
   * @returns {Window|null} The opened window reference, or null.
   */
  const openAuthWindow = (authUrl, options = {}) => {
    if (!authUrl) return null

    initBroadcastChannel()

    const opts = typeof options === 'boolean' ? { asTab: options } : options
    const shouldOpenAsTab = opts.asTab ?? (opts.marketplace?.toLowerCase() === 'lazada')

    const windowFeatures = shouldOpenAsTab
      ? undefined
      : (opts.windowFeatures ?? getIntegrationPopupWindowFeatures())

    const authWindow = window.open(authUrl, '_blank', windowFeatures)

    if (authWindow) {
      activeAuthWindows.push(authWindow)
    }

    return authWindow
  }

  /**
   * Fetches the `auth_url` from the marketplace endpoint and opens the authentication popup.
   * @param {'shopee'|'tiktok'|string} marketplace - The marketplace identifier.
   * @param {{ asTab?: boolean, windowFeatures?: string }} [options] - Additional window options.
   * @returns {Promise<{ authUrl: string, response: unknown }>}
   */
  const requestMarketplaceAuthUrl = async (marketplace, options = {}) => {
    const endpoint = getMarketplaceAuthEndpoint(marketplace)

    if (!endpoint) {
      throw new Error(`Marketplace "${marketplace}" is not supported`)
    }

    const response = await vm.$http_new.post(endpoint)
    const authUrl = response?.data?.data?.auth_url

    if (!authUrl) {
      throw new Error('auth_url not found in response')
    }

    openAuthWindow(authUrl, { marketplace, ...options })

    return {
      authUrl,
      response: response?.data,
    }
  }

  return {
    activeAuthWindows,
    getMarketplaceAuthEndpoint,
    getIntegrationPopupWindowFeatures,
    closeAllActiveAuthWindows,
    initBroadcastChannel,
    closeBroadcastChannel,
    openAuthWindow,
    requestMarketplaceAuthUrl,
    handleIntegrationMessage,
  }
}
